/**
 * Resolves campaign preview state from UIMessages (canonical) + transient socket state.
 */

import {
  getArtResultsFromMessages,
  getProcessingToolPart,
  getStrategyMarkdownFromMessages,
  hasProcessingToolInMessages,
  isArtToolProcessing,
  hasArtToolActivityInMessages,
  getExpectedArtCount,
  mergeArtResultsByPiece,
  normalizeArtResultsList,
} from './toolPartSelectors';
import {isInputLockedForTools} from './toolPartSelectors';
import {CAMPAIGN_WIDGET_TOOL_NAMES} from '../utils/campaignCompletionMessages';
import {
  CAMPAIGN_TYPE_UI_TOOL_NAME,
  LOGO_UI_TOOL_NAME,
  PARAMETERIZATION_TOOL_NAMES,
  WIDGET_INPUT_LOCK_TOOL_NAMES,
} from './campaignToolCatalog';
import {findToolPart} from './toolPartSelectors';

export {
  isArtToolPartType,
  hasArtToolActivityInMessages,
  isArtToolProcessing,
} from './toolPartSelectors';

export function getStrategyMarkdown(messages, {socketMarkdown = '', sessionMarkdown = ''} = {}) {
  const fromParts = getStrategyMarkdownFromMessages(messages);
  return (
    fromParts?.trim() ||
    socketMarkdown?.trim() ||
    sessionMarkdown?.trim() ||
    ''
  );
}

function normalizeSessionArtMedias(medias) {
  if (!Array.isArray(medias)) {
    return [];
  }
  return normalizeArtResultsList(
    medias.filter(
      (item) =>
        item &&
        typeof item === 'object' &&
        typeof item.pieceNumber === 'number',
    ),
  );
}

export function getArtPreviewState(
  messages,
  {
    socketResults = [],
    socketStatus = 'idle',
    socketProgress = {current: 0, total: 0, progress: 0},
    sessionDetail = null,
  } = {},
) {
  const artResultsFromParts = getArtResultsFromMessages(messages);
  const socketNormalized = normalizeArtResultsList(socketResults);
  const sessionMedias = normalizeSessionArtMedias(sessionDetail?.medias);

  let resolvedArtResults = artResultsFromParts;
  if (resolvedArtResults.length === 0 && sessionMedias.length > 0) {
    resolvedArtResults = sessionMedias;
  } else if (sessionMedias.length > 0) {
    resolvedArtResults = mergeArtResultsByPiece(
      resolvedArtResults,
      sessionMedias,
    );
  }

  if (socketNormalized.length > 0) {
    resolvedArtResults = mergeArtResultsByPiece(
      resolvedArtResults,
      socketNormalized,
    );
  }

  const artProcessingFromMessages = isArtToolProcessing(messages);

  let resolvedArtStatus = socketStatus;
  if (artProcessingFromMessages) {
    resolvedArtStatus =
      socketStatus === 'editing' || socketStatus === 'generating'
        ? socketStatus
        : 'generating';
  } else if (resolvedArtResults.length > 0) {
    resolvedArtStatus =
      socketStatus === 'editing' ? 'editing' : 'generated';
  } else if (
    !hasArtToolActivityInMessages(messages) &&
    socketStatus !== 'generating' &&
    socketStatus !== 'editing'
  ) {
    resolvedArtStatus = 'idle';
  }

  const expectedTotal = getExpectedArtCount(messages, sessionDetail);
  const resolvedProgress = {
    ...socketProgress,
    total:
      socketProgress.total > 0
        ? socketProgress.total
        : expectedTotal ?? resolvedArtResults.length ?? 0,
  };

  const showArtsTab =
    resolvedArtStatus === 'generating' ||
    resolvedArtStatus === 'editing' ||
    resolvedArtResults.length > 0;

  return {
    artResults: resolvedArtResults,
    artStatus: resolvedArtStatus,
    artProgress: resolvedProgress,
    showArtsTab,
  };
}

/** @deprecated Use getStrategyMarkdown + getArtPreviewState */
export function resolveSessionPreviewData({
  messages,
  strategyFromSession = '',
  strategyMarkdown = '',
  artResults = [],
  artStatus = 'idle',
  artProgress = {current: 0, total: 0, progress: 0},
  sessionDetail = null,
}) {
  const strategy = getStrategyMarkdown(messages, {
    socketMarkdown: strategyMarkdown,
    sessionMarkdown: strategyFromSession,
  });
  const art = getArtPreviewState(messages, {
    socketResults: artResults,
    socketStatus: artStatus,
    socketProgress: artProgress,
    sessionDetail,
  });

  return {
    strategyMarkdown: strategy,
    ...art,
  };
}

export function shouldShowCampaignCanvas({
  messages,
  strategyFromSession = '',
  strategyMarkdown = '',
}) {
  const strategy = getStrategyMarkdown(messages, {
    socketMarkdown: strategyMarkdown,
    sessionMarkdown: strategyFromSession,
  });
  return Boolean(strategy?.trim());
}

export function isProcessing(messages, {socketProcessing = false} = {}) {
  return socketProcessing || hasProcessingToolInMessages(messages);
}

const CHAT_NON_BLOCKING_PROCESSING_TOOLS = ['editar_documento_estrategia'];

function isNonBlockingChatProcessingTool(toolName) {
  return CHAT_NON_BLOCKING_PROCESSING_TOOLS.includes(toolName);
}

function resolveProcessingToolName(processingPart, processingToolName = null) {
  if (processingToolName) {
    return processingToolName;
  }
  if (!processingPart) {
    return null;
  }
  if (processingPart.output?.toolName) {
    return processingPart.output.toolName;
  }
  if (processingPart.type?.startsWith('tool-')) {
    return processingPart.type.slice('tool-'.length);
  }
  return null;
}

/** Blocks chat input during async tools — except strategy edits (user may keep typing). */
export function isChatAsyncProcessingLocked(
  messages,
  {
    socketProcessing = false,
    processingToolName = null,
    campaignStatus = null,
  } = {},
) {
  const processingPart = getProcessingToolPart(messages);
  const messageToolName = resolveProcessingToolName(processingPart);
  const socketToolName = resolveProcessingToolName(null, processingToolName);
  const effectiveToolName = socketToolName ?? messageToolName;

  if (socketProcessing) {
    if (effectiveToolName) {
      return !isNonBlockingChatProcessingTool(effectiveToolName);
    }
    if (campaignStatus === 'editing_strategy') {
      return false;
    }
    return true;
  }

  if (!processingPart) {
    return false;
  }

  return !isNonBlockingChatProcessingTool(messageToolName);
}

export function isParameterizationInputLocked(messages) {
  return isInputLockedForTools(messages, PARAMETERIZATION_TOOL_NAMES);
}

export function isCampaignTypeInputLocked(messages) {
  return isInputLockedForTools(messages, [CAMPAIGN_TYPE_UI_TOOL_NAME]);
}

export function isCampaignWidgetInputLocked(messages) {
  return isInputLockedForTools(messages, WIDGET_INPUT_LOCK_TOOL_NAMES);
}

export function isSessionReadOnly(sessionDetail) {
  return sessionDetail?.status === 'completed';
}

export function isChatInputLocked(messages, sessionDetail = null) {
  if (isSessionReadOnly(sessionDetail)) {
    return true;
  }

  const hasLocalMessages = Array.isArray(messages) && messages.length > 0;
  if (hasLocalMessages) {
    return (
      isParameterizationInputLocked(messages) ||
      isCampaignTypeInputLocked(messages) ||
      isCampaignWidgetInputLocked(messages)
    );
  }

  const sessionMessages = Array.isArray(sessionDetail?.messages)
    ? sessionDetail.messages
    : [];

  return (
    isParameterizationInputLocked(sessionMessages) ||
    isCampaignTypeInputLocked(sessionMessages) ||
    isCampaignWidgetInputLocked(sessionMessages)
  );
}

export function getProcessingToolPartFromMessages(messages) {
  return getProcessingToolPart(messages);
}

const ATTACH_REQUEST_TOOL_NAMES = new Set([
  LOGO_UI_TOOL_NAME,
  CAMPAIGN_WIDGET_TOOL_NAMES.IMAGENS_CAMPANHA,
]);

/**
 * Finds the most recent logo/images attach request tool part (any status).
 */
export function findLatestAttachRequestPart(messages) {
  if (!Array.isArray(messages)) {
    return null;
  }

  let latest = null;

  messages.forEach((msg) => {
    if (msg.role !== 'assistant') {
      return;
    }

    (msg.parts || []).forEach((part) => {
      if (part.state !== 'output-available') {
        return;
      }

      const toolName = part.type?.startsWith('tool-')
        ? part.type.slice(5)
        : null;

      if (toolName && ATTACH_REQUEST_TOOL_NAMES.has(toolName)) {
        latest = {part, toolName};
      }
    });
  });

  return latest;
}

function resolveAttachModeFromLatestPart(latest, {isParameterizationComplete = false} = {}) {
  if (!latest) {
    return null;
  }

  const status = latest.part.output?.status;
  if (status === 'completed' || status === 'error' || status !== 'awaiting_selection') {
    return null;
  }

  if (latest.toolName === LOGO_UI_TOOL_NAME) {
    return isParameterizationComplete ? null : 'logo';
  }

  if (latest.toolName === CAMPAIGN_WIDGET_TOOL_NAMES.IMAGENS_CAMPANHA) {
    return 'images';
  }

  return null;
}

/**
 * Controls which attach menu option is available in the chat input.
 * Driven by the most recent agent tool part awaiting user input.
 * @returns {'logo' | 'images' | 'referenceArt' | null}
 */
export function resolveChatAttachMode(messages, options = {}) {
  const {
    selectedArt = null,
    isParameterizationComplete = false,
    sessionMessages = [],
  } = options;

  if (selectedArt) {
    return 'referenceArt';
  }

  const localLatest = findLatestAttachRequestPart(messages);
  if (localLatest) {
    return resolveAttachModeFromLatestPart(localLatest, {
      isParameterizationComplete,
    });
  }

  const sessionLatest = findLatestAttachRequestPart(sessionMessages);
  return resolveAttachModeFromLatestPart(sessionLatest, {
    isParameterizationComplete,
  });
}

export function isCampaignPhaseStarted(messages) {
  return Boolean(
    findToolPart(messages, CAMPAIGN_TYPE_UI_TOOL_NAME, {
      statuses: ['awaiting_selection', 'completed'],
    }),
  );
}

/** Mirrors backend CAMPAIGN_FLOW_TEXT_RESPONSES.artsEdit */
export const CAMPAIGN_FLOW_ARTS_EDIT_PROMPT =
  'Descreva as alterações que deseja aplicar nas artes (edição em lote).';

/** Mirrors backend CAMPAIGN_FLOW_TEXT_RESPONSES.artsReject */
export const CAMPAIGN_FLOW_ARTS_REJECT_PROMPT =
  'Descreva o motivo da reprovação e o que deseja alterar para a nova geração das artes.';

function normalizePromptText(value) {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ');
}

function getLastAssistantText(messages) {
  if (!Array.isArray(messages)) {
    return '';
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const msg = messages[index];
    if (msg.role !== 'assistant') {
      continue;
    }

    return (msg.parts ?? [])
      .filter((part) => part.type === 'text' && typeof part.text === 'string')
      .map((part) => part.text)
      .join('\n')
      .trim();
  }

  return '';
}

export function isAwaitingBatchEditInstruction(messages) {
  const lastAssistantText = getLastAssistantText(messages);
  return (
    normalizePromptText(lastAssistantText) ===
    normalizePromptText(CAMPAIGN_FLOW_ARTS_EDIT_PROMPT)
  );
}

export function isAwaitingBatchRejectInstruction(messages) {
  const lastAssistantText = getLastAssistantText(messages);
  return (
    normalizePromptText(lastAssistantText) ===
    normalizePromptText(CAMPAIGN_FLOW_ARTS_REJECT_PROMPT)
  );
}

function hasScheduleWidgetInTranscript(messages) {
  return (messages || []).some((message) =>
    (message.parts || []).some((part) => {
      if (
        part.type ===
        `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.CONFIGURAR_AGENDAMENTO}`
      ) {
        return ['awaiting_selection', 'completed'].includes(part.output?.status);
      }
      if (
        part.type === `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.EXECUTAR_AGENDAMENTO}`
      ) {
        return ['processing', 'completed', 'error'].includes(
          part.output?.status,
        );
      }
      return false;
    }),
  );
}

function isSchedulingExecutionRunning(messages, runtime) {
  const fromMessages = (messages || []).some((message) =>
    (message.parts || []).some(
      (part) =>
        part.type ===
          `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.EXECUTAR_AGENDAMENTO}` &&
        part.output?.status === 'processing',
    ),
  );

  if (fromMessages) {
    return true;
  }

  return (
    runtime?.operation?.family === 'scheduling' &&
    Boolean(runtime?.processing?.isProcessing)
  );
}

/** Mirrors ChatSocket schedule UX gates (calendar tab, read-only schedule fields). */
export function resolveScheduleUxState({
  messages = [],
  runtime = null,
  socialScheduleContext = {},
  hasSelectedSchedulePublicationItem = false,
} = {}) {
  const socialSelection = socialScheduleContext?.socialSelection || [];
  const selectedNetworks = socialScheduleContext?.selectedNetworks || [];
  const hasAuthenticatedSocialMedia =
    socialSelection.length > 0 || selectedNetworks.length > 0;

  const isSchedulingRunning = isSchedulingExecutionRunning(messages, runtime);
  const isSchedulingContextActive =
    hasScheduleWidgetInTranscript(messages) ||
    runtime?.operation?.family === 'scheduling';

  const canEditPublicationSchedule =
    hasAuthenticatedSocialMedia && !isSchedulingRunning;
  const isPublicationReadOnly = !canEditPublicationSchedule;
  const isCalendarPreviewEnabled =
    hasAuthenticatedSocialMedia &&
    hasSelectedSchedulePublicationItem &&
    (isSchedulingContextActive ||
      isSchedulingRunning ||
      canEditPublicationSchedule);

  return {
    hasAuthenticatedSocialMedia,
    isSchedulingRunning,
    isSchedulingContextActive,
    canEditPublicationSchedule,
    isPublicationReadOnly,
    isCalendarPreviewEnabled,
  };
}

export function normalizeSchedulePreviewArt(art = {}) {
  const pieceNumber = art.piece_number ?? art.pieceNumber;
  return {
    ...art,
    piece_number: pieceNumber,
    pieceNumber,
    imageUrl: art.imageUrl || art.url,
    url: art.url || art.imageUrl,
    title: art.title || art.piece_name || art.pieceName,
  };
}

export function canSubmitArtSingleEdit({
  selectedArt,
  runtime,
  sessionDetail,
}) {
  if (!selectedArt || isSessionReadOnly(sessionDetail)) {
    return false;
  }

  if (runtime?.processing?.isProcessing) {
    return false;
  }

  const reason = runtime?.input?.reason;
  if (reason === 'session_completed' || reason === 'submitting') {
    return false;
  }

  if (reason === 'processing') {
    return false;
  }

  return true;
}
